import { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Upload, Star, CheckCircle } from 'lucide-react';
import type { ChatbotMessageResponse } from '../types';
import { api } from '../services/api';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestedActions?: string[];
}


// ── Inline rating upload panel ───────────────────────────────────────────────
interface RatingUploadPanelProps {
  onSuccess: (msg: string) => void;
  onCancel: () => void;
}

const PLATFORMS_LIST = ['Trustpilot', 'Google', 'Other'] as const;
type Platform = typeof PLATFORMS_LIST[number];

function RatingUploadPanel({ onSuccess, onCancel }: RatingUploadPanelProps) {
  const [platform, setPlatform] = useState<Platform>('Trustpilot');
  const [file, setFile]         = useState<File | null>(null);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (!file) { setErr('Please select a screenshot.'); return; }
    setErr(null);
    setBusy(true);
    const form = new FormData();
    form.append('screenshot', file);
    form.append('platform', platform);
    try {
      await api.post('/ratings/screenshots', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSuccess(
        `Your ${platform} rating screenshot has been submitted for verification. ` +
        `Our support team will review it and you'll be notified once it's approved.`
      );
    } catch (e: any) {
      setErr(e?.response?.data?.title ?? e?.response?.data ?? 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-3 text-sm">
      <p className="font-medium text-blue-800 flex items-center gap-1.5">
        <Star className="w-4 h-4" />Upload Third-party Platform Rating
      </p>

      <div className="flex gap-1.5 flex-wrap">
        {PLATFORMS_LIST.map(p => (
          <button key={p} type="button" onClick={() => setPlatform(p)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
              platform === p ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'
            }`}>{p}</button>
        ))}
      </div>

      <div
        className="border-2 border-dashed border-blue-300 rounded-lg py-3 text-center cursor-pointer hover:border-blue-500 transition-colors"
        onClick={() => inputRef.current?.click()}
      >
        {file
          ? <p className="text-xs text-blue-700 font-medium">{file.name} ({(file.size / 1024).toFixed(0)} KB)</p>
          : <><Upload className="w-4 h-4 text-blue-400 mx-auto mb-1" /><p className="text-xs text-blue-500">Click to choose screenshot (PNG/JPEG, max 5 MB)</p></>
        }
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp"
        className="hidden" onChange={e => { setFile(e.target.files?.[0] ?? null); setErr(null); }} />

      {err && <p className="text-xs text-red-600">{err}</p>}

      <div className="flex gap-2">
        <button onClick={submit} disabled={busy || !file}
          className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400
                     text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
          {busy
            ? <><span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />Uploading…</>
            : <><Upload className="w-3 h-3" />Submit</>
          }
        </button>
        <button onClick={onCancel}
          className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Chatbot props ─────────────────────────────────────────────────────────────

interface ChatbotProps {
  sendMessage: (msg: string, sessionId?: string) => Promise<ChatbotMessageResponse>;
  userId?: string;
  initialGreeting?: string;
  onJobDataExtracted?: (data: ChatbotMessageResponse['extractedJobData']) => void;
  onNavigate?: (path: string) => void;
}

const RATING_KEYWORDS = [
  'trustpilot', 'google review', 'external rating', 'platform rating',
  'upload my rating', 'verify my rating', 'screenshot rating',
  'upload screenshot', 'rating screenshot', 'third-party rating',
];

function isRatingIntent(msg: string) {
  const lower = msg.toLowerCase();
  return RATING_KEYWORDS.some(k => lower.includes(k));
}

const hasSpeechRecognition = typeof window !== 'undefined' &&
  !!(window.SpeechRecognition ?? window.webkitSpeechRecognition);

export function TransitDealChatbot({
  sendMessage,
  userId,
  initialGreeting = "Hi! I'm the TransitDeal assistant. I can help you post a job, track your deliveries, or answer any questions. What can I help you with today?",
  onJobDataExtracted,
  onNavigate,
}: ChatbotProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '0',
      role: 'assistant',
      content: initialGreeting,
      timestamp: new Date(),
      suggestedActions: [
        'Post a delivery job',
        'Track my deliveries',
        'Upload my platform rating',
        'Pricing & fees',
      ],
    },
  ]);

  const [showRatingUpload, setShowRatingUpload] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => window.speechSynthesis.cancel();
  }, []);

  const speakText = useCallback((text: string) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-GB';
    utterance.rate = 0.95;
    utterance.volume = 1;
    window.speechSynthesis.speak(utterance);
  }, []);

  const handleSend = useCallback(async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    // Intercept rating upload intent — show inline upload panel
    if (isRatingIntent(messageText)) {
      setMessages(prev => [
        ...prev,
        { id: Date.now().toString(), role: 'user', content: messageText, timestamp: new Date() },
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: "Sure! You can upload a screenshot of your Trustpilot, Google, or other third-party platform rating below. Our support team will verify it and display your approved rating on your profile.",
          timestamp: new Date(),
        },
      ]);
      setInput('');
      setShowRatingUpload(true);
      return;
    }

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await sendMessage(messageText, sessionId);
      setSessionId(response.sessionId);
      setProfileCompleteness(response.profileCompleteness);

      if (response.extractedJobData) {
        onJobDataExtracted?.(response.extractedJobData);
      }

      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.reply,
        timestamp: new Date(),
        suggestedActions: response.suggestedActions,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      if (ttsEnabled) speakText(response.reply);
    } catch (err) {
      console.error('[Chatbot] sendMessage failed:', err);
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "Sorry, I couldn't process that right now. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, sessionId, sendMessage, onJobDataExtracted, ttsEnabled, speakText]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }

    const SpeechRecognitionAPI = window.SpeechRecognition ?? window.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      console.error('[Chatbot] Web Speech API not supported in this browser');
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[0][0].transcript.trim();
      setIsRecording(false);
      if (transcript) handleSend(transcript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setIsRecording(false);
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        console.error('[Chatbot] Speech recognition error:', event.error);
      }
    };

    recognition.onend = () => setIsRecording(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  }, [isRecording, handleSend]);

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-blue-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center">
            <span className="text-blue-700 font-bold text-sm">TD</span>
          </div>
          <div>
            <p className="text-white font-semibold text-sm">TransitDeal Assistant</p>
            <p className="text-blue-200 text-xs">Powered by AI · Always here to help</p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {userId && profileCompleteness > 0 && (
            <div className="flex flex-col items-end">
              <span className="text-blue-200 text-xs">Profile</span>
              <div className="flex items-center gap-1">
                <div className="w-20 h-1.5 bg-blue-900 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-400 rounded-full transition-all duration-500"
                    style={{ width: `${profileCompleteness}%` }}
                  />
                </div>
                <span className="text-white text-xs font-medium">{profileCompleteness}%</span>
              </div>
            </div>
          )}
          <button
            onClick={() => {
              const enabling = !ttsEnabled;
              setTtsEnabled(enabling);
              if (enabling) {
                // iOS Safari: speechSynthesis must be unlocked within a user gesture.
                // Speak a silent utterance now so later async calls are allowed.
                const unlock = new SpeechSynthesisUtterance('');
                window.speechSynthesis.speak(unlock);
                setTimeout(() => window.speechSynthesis.cancel(), 50);
              } else {
                window.speechSynthesis.cancel();
              }
            }}
            title={ttsEnabled ? 'Mute voice replies' : 'Enable voice replies'}
            className={`text-lg leading-none p-1 rounded transition-opacity ${ttsEnabled ? 'opacity-100' : 'opacity-40'}`}
          >
            {ttsEnabled ? '🔊' : '🔇'}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
              {/* Bubble */}
              <div
                className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-700 text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}
              >
                {msg.content}
              </div>

              {/* Timestamp */}
              <p className={`text-xs text-gray-400 mt-1 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>

              {/* Suggested actions — the startup message, or whichever assistant message is
                  currently last (e.g. the post-rating-upload success message below, which
                  attaches its own 'View my ratings' / 'Post a delivery job' actions). Restricting
                  this to only the startup message (id === '0') made every later suggestedActions
                  array, including that one, permanently unrenderable dead code. */}
              {msg.role === 'assistant' && (msg.id === '0' || msg === messages[messages.length - 1])
                && msg.suggestedActions && msg.suggestedActions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {msg.suggestedActions.map((action) => (
                    <button
                      key={action}
                      onClick={() => {
                        if (action === 'View my ratings' && onNavigate) {
                          onNavigate('/my-ratings');
                        } else {
                          handleSend(action);
                        }
                      }}
                      className="text-xs px-3 py-1.5 rounded-full border border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              )}

              {/* Inline rating upload panel — appears after bot acknowledges intent */}
              {msg.role === 'assistant' && showRatingUpload && msg === messages[messages.length - 1] && (
                <RatingUploadPanel
                  onSuccess={successMsg => {
                    setShowRatingUpload(false);
                    setMessages(prev => [
                      ...prev,
                      {
                        id: Date.now().toString(),
                        role: 'assistant',
                        content: successMsg,
                        timestamp: new Date(),
                        suggestedActions: ['View my ratings', 'Post a delivery job'],
                      },
                    ]);
                  }}
                  onCancel={() => {
                    setShowRatingUpload(false);
                    setMessages(prev => [
                      ...prev,
                      {
                        id: Date.now().toString(),
                        role: 'assistant',
                        content: "No problem! You can upload your rating screenshot any time from the My Ratings page. Is there anything else I can help you with?",
                        timestamp: new Date(),
                      },
                    ]);
                  }}
                />
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 p-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend(input)}
            placeholder="Type a message..."
            disabled={isLoading || isRecording}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:opacity-50"
          />
          {hasSpeechRecognition && (
            <button
              onClick={toggleRecording}
              disabled={isLoading}
              title={isRecording ? 'Stop recording' : 'Record voice message'}
              className={`p-2.5 rounded-xl transition-colors disabled:opacity-50 ${
                isRecording
                  ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }`}
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
          )}
          <button
            onClick={() => handleSend(input)}
            disabled={isLoading || !input.trim() || isRecording}
            className="px-4 py-2.5 bg-blue-700 hover:bg-blue-800 disabled:bg-gray-300 text-white rounded-xl transition-colors text-sm font-medium"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
